import { dateTimeRangeFormat } from "@sinch/utils/dateTime/dateFns";
import { useMemo } from "react";
import { IntlShape, useIntl } from "react-intl";
import { useSettings } from "../providers/AppSettings";
import { ExpandedFormats, FormatScope, FormatterFn, NamedFormats } from "./format.types";
import { useFormatAddress } from "@sinch/core/hooks/formatters/useFormatAddress";
import { useFormatByteUnits } from "@sinch/core/hooks/formatters/useFormatByteUnits";
import { useFormatDuration } from "@sinch/core/hooks/formatters/useFormatDuration";
import { useFormatList } from "@sinch/core/hooks/formatters/useFormatList";
import { useFormatPhone } from "@sinch/core/hooks/formatters/useFormatPhone";

/**
 * Assign defined formats to formatting functions for easier calling formatting functions
 */
const assignFormatter = <
  Scope extends FormatScope,
  Method extends FormatterFn<Method> = (...args: unknown[]) => string,
>(
  formats: Record<string, unknown>,
  formatter: Method
): NamedFormats<Scope, Method> =>
  // For each format, create method with name of format and formatted method
  Object.keys(formats).reduce<NamedFormats<Scope, Method>>(
    (carry, key) => ({
      ...carry,
      [key]: (value: Parameters<Method>[0], additionalConfig?: Parameters<Method>[1]) =>
        formatter(value, { ...additionalConfig, format: key }),
    }),
    {} as NamedFormats<Scope, Method>
  );

/**
 * Hook for providing formatting methods for specified scopes,
 * @see frontend/src/core/Config/formats.tsx for format definition
 */
export const useFormat = () => {
  const { country } = useSettings();
  const { formatDate, formatTime, formatNumber, formats, locale } = useIntl() as IntlShape & {
    formats: ExpandedFormats;
  };

  const formatDuration = useFormatDuration();
  const formatByteUnits = useFormatByteUnits();
  const formatPhone = useFormatPhone();
  const formatList = useFormatList();
  const formatAddress = useFormatAddress();

  return useMemo(() => {
    // Create objects with formatting functions
    const time = assignFormatter<"time", typeof formatTime>(formats?.time || {}, formatTime);
    const date = assignFormatter<"date", typeof formatDate>(formats?.date || {}, formatDate);
    const number = assignFormatter<"number", typeof formatNumber>(formats?.number || {}, formatNumber);
    const list = assignFormatter<"list", typeof formatList>(formats?.list || {}, formatList);
    const duration = assignFormatter<"duration", typeof formatNumber>(formats?.duration || {}, formatDuration);
    const unit = assignFormatter<"bytes", typeof formatNumber>(formats?.bytes || {}, formatByteUnits);
    const phone = assignFormatter<"phone", typeof formatPhone>(formats?.phone || {}, formatPhone);
    const address = assignFormatter<"address", typeof formatAddress>(formats?.address || {}, formatAddress);

    return {
      date,
      time,
      num: number,
      list,
      duration,
      unit,
      phone,
      address,
    };
  }, [formatDate, formatNumber, formatTime, formats, locale, country]);
};
type AdaptableUnit = "hour" | "minute" | "second";
/**
 * Simplify long duration to short or narrow when there is fraction of unit, eg. 1 hour 30 minutes -> 1 hr 30 min
 */
export const useAdaptableDuration = () => {
  const { duration } = useFormat();
  return (value: number, unit?: AdaptableUnit) => {
    if (unit === "second") {
      return duration.long(value);
    }
    if (unit === "minute") {
      return value % 60 === 0 ? duration.long(value) : duration.short(value);
    }
    const shortOrNarrow = value % 60 === 0 ? duration.short(value) : duration.narrow(value);
    return value % 3600 === 0 ? duration.long(value) : shortOrNarrow;
  };
};

type FormatMethods = ReturnType<typeof useFormat>;
/**
 * Format date range. IF start and end date are the same, only time is shown, otherwise date and time are shown
 */
export const useDateRangeFormat = (options?: {
  timeFormatter?: FormatMethods["time"][keyof FormatMethods["time"]];
  dateFormatter?: FormatMethods["date"][keyof FormatMethods["date"]];
}) => {
  const { date, time } = useFormat();
  const formatTime = options?.timeFormatter ?? time.short;
  const formatDate = options?.dateFormatter ?? date.short;
  return (start: Date, end: Date) =>
    dateTimeRangeFormat(formatTime(start), formatDate(start), formatTime(end), formatDate(end));
};
